import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { DateTime } from 'luxon';
import {
  getSessionCountdown,
  SessionCountdown,
  getNextSessionEventDate,
} from '@black/shared';

import { State } from '../../shared/core/state';
import { User, UserRoles } from '../../shared/core/state/users/models';
import { TenantService, TenantInfo } from '../../shared/services/tenant.service';
import { BatchService } from '../../shared/services/batch.service';
import { CourseService } from '../../shared/core/state/course/course.service';
import { SubscriptionService } from '../../shared/core/state/subscription/subscription.service';
import { LiveClassService } from '../../shared/core/state/live-class/live-class.service';
import { UserService } from '../../shared/core/state/users/users.service';
import {
  MeetingEvents,
  LiveClass,
  Meeting,
} from '../../shared/core/state/live-class/models/live-class';

export interface NextSessionItem {
  liveClassId: string;
  liveClassName: string;
  meetingId: string;
  meetingName: string;
  startsAt: Date;
  durationInMinutes: number;
  meetingProvider?: string;
  shareLinkId?: string;
  directJoinUrl?: string;
  countdown: SessionCountdown;
  isLive: boolean;
  canJoin: boolean;
}

@Component({
  selector: 'black-dashboard-overview',
  templateUrl: './dashboard-overview.component.html',
  styleUrls: ['./dashboard-overview.component.css'],
  standalone: false,
})
export class DashboardOverviewComponent implements OnInit, OnDestroy {
  private store = inject<Store<State>>(Store);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private tenantService = inject(TenantService);
  private batchService = inject(BatchService);
  private courseService = inject(CourseService);
  private subscriptionService = inject(SubscriptionService);
  private liveClassService = inject(LiveClassService);
  private userService = inject(UserService);

  user: Partial<User> | null = null;
  currentTenant: TenantInfo | null = null;

  isLoading = true;
  activeRoleTab: 'TEACHING' | 'LEARNING' = 'LEARNING';

  // Student metrics
  batchesCount = 0;
  classesCount = 0;
  coursesCount = 0;
  nextSession: NextSessionItem | null = null;
  todaySessionsCount = 0;

  // Instructor metrics
  instructorClassesCount = 0;
  instructorBatchesCount = 0;
  instructorCoursesCount = 0;
  totalStudentsCount = 0;
  nextTeachingSession: NextSessionItem | null = null;

  private subscription = new Subscription();
  private timerHandle: any = null;

  ngOnInit(): void {
    this.subscription.add(
      this.store
        .select((state) => state.users.user)
        .subscribe((user) => {
          this.user = user;
          if (
            user?.role === UserRoles.INSTRUCTOR ||
            user?.role === UserRoles.ADMIN
          ) {
            this.activeRoleTab = 'TEACHING';
          }
        }),
    );

    this.subscription.add(
      this.tenantService.currentTenant$.subscribe((tenant) => {
        this.currentTenant = tenant;
      }),
    );

    this.loadDashboardData();

    if (isPlatformBrowser(this.platformId)) {
      this.timerHandle = setInterval(() => {
        const now = new Date();
        const refresh = (s: NextSessionItem | null) => {
          if (!s) return;
          const u = getSessionCountdown(s.startsAt, { now });
          s.countdown = u;
          s.isLive = u.status === 'LIVE';
          s.canJoin = u.canJoin || s.isLive;
        };
        refresh(this.nextSession);
        refresh(this.nextTeachingSession);
      }, 15000);
    }
  }

  ngOnDestroy(): void {
    if (this.timerHandle) {
      clearInterval(this.timerHandle);
    }
    this.subscription.unsubscribe();
  }

  setRoleTab(tab: 'TEACHING' | 'LEARNING'): void {
    this.activeRoleTab = tab;
  }

  loadDashboardData(): void {
    this.isLoading = true;

    const today = new Date();
    const from = DateTime.fromJSDate(today)
      .startOf('day')
      .toJSDate()
      .toISOString();
    const to = DateTime.fromJSDate(today)
      .plus({ days: 14 })
      .toJSDate()
      .toISOString();

    const isInstructor = this.isInstructor;

    forkJoin({
      // Instructor data (only when instructor)
      instructorClasses: isInstructor
        ? this.liveClassService
            .getAllForInstructor()
            .pipe(catchError(() => of({ docs: [] })))
        : of({ docs: [] }),
      instructorCourses: isInstructor
        ? this.courseService
            .getAllForInstructor()
            .pipe(catchError(() => of({ docs: [] })))
        : of({ docs: [] }),
      batches: this.batchService
        .getBatches(1, 100)
        .pipe(catchError(() => of([]))),
      students: isInstructor
        ? this.userService
            .getAll(1, 1, { role: 'STUDENT' })
            .pipe(catchError(() => of({ totalDocs: 0 })))
        : of({ totalDocs: 0 }),

      // Student learning data
      enrollments: this.courseService
        .getEnrollments()
        .pipe(catchError(() => of([]))),
      subscriptions: this.subscriptionService
        .fetchAllSubscribtions()
        .pipe(catchError(() => of([]))),
      sessions: this.liveClassService
        .fetchAllMeetingSessions(from, to, true)
        .pipe(catchError(() => of([]))),
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: ({
          instructorClasses,
          instructorCourses,
          batches,
          students,
          enrollments,
          subscriptions,
          sessions,
        }: any) => {
          // 1. Batches
          const batchList = Array.isArray(batches)
            ? batches
            : (batches?.docs ?? []);
          this.batchesCount = batchList.length;
          this.instructorBatchesCount = batchList.length;

          // 2. Instructor metrics
          const instClasses: LiveClass[] = Array.isArray(instructorClasses)
            ? instructorClasses
            : (instructorClasses?.docs ?? []);
          this.instructorClassesCount = instClasses.length;

          const instCourses = Array.isArray(instructorCourses)
            ? instructorCourses
            : (instructorCourses?.docs ?? []);
          this.instructorCoursesCount = instCourses.length;

          this.totalStudentsCount = students?.totalDocs ?? 0;

          // Process next session to teach
          this.processInstructorSessions(instClasses);

          // 3. Student metrics
          const courseList = Array.isArray(enrollments) ? enrollments : [];
          this.coursesCount = courseList.length;

          const subList = Array.isArray(subscriptions)
            ? subscriptions
            : (subscriptions?.docs ?? []);
          const activeSubs = subList.filter(
            (s: any) => s.status === 'ACTIVE' || s.status === 'COMPLETE',
          );
          this.classesCount = activeSubs.length;

          // Process next session to attend as student
          this.processStudentSessions(sessions || []);
        },
      });
  }

  private processInstructorSessions(classes: LiveClass[]): void {
    const list: NextSessionItem[] = [];
    const now = new Date();

    for (const lc of classes) {
      if (!lc.meetings?.length) continue;
      for (const meeting of lc.meetings) {
        const nextDate = getNextSessionEventDate(meeting as any, { now });
        if (!nextDate) continue;

        const countdown = getSessionCountdown(nextDate, {
          startTime: meeting.startTime,
          endTime: meeting.endTime,
          timezone: meeting.timezone,
          now,
        });

        if (countdown.status === 'ENDED') continue;

        const isLive = countdown.status === 'LIVE';
        const canJoin = countdown.canJoin || isLive;

        list.push({
          liveClassId: lc.id || (lc as any)._id,
          liveClassName: lc.title || (lc as any).name || 'Live Class',
          meetingId: meeting.id || (meeting as any)._id,
          meetingName: meeting.name,
          startsAt: nextDate,
          durationInMinutes: 60,
          meetingProvider: meeting.provider,
          shareLinkId: meeting.shareLinkId,
          directJoinUrl: meeting.directJoinUrl,
          countdown,
          isLive,
          canJoin,
        });
      }
    }

    list.sort((a, b) => {
      if (a.isLive && !b.isLive) return -1;
      if (!a.isLive && b.isLive) return 1;
      return a.startsAt.getTime() - b.startsAt.getTime();
    });

    this.nextTeachingSession = list.length > 0 ? list[0] : null;
  }

  private processStudentSessions(meetingEvents: MeetingEvents[]): void {
    const list: NextSessionItem[] = [];
    const now = new Date();
    let todayCount = 0;

    for (const group of meetingEvents) {
      if (!group.events?.length) continue;
      for (const ev of group.events) {
        if (ev.isAccessible === false) continue;

        const startsAt = new Date(ev.startsAt);
        const countdown = getSessionCountdown(startsAt, { now });

        if (countdown.isSessionDay) {
          todayCount++;
        }

        if (countdown.status === 'ENDED') continue;

        const isLive = countdown.status === 'LIVE';
        const canJoin = countdown.canJoin || isLive;

        list.push({
          liveClassId: group.liveClassId,
          liveClassName: group.liveClassName,
          meetingId: ev.meetingId,
          meetingName: ev.meetingName,
          startsAt,
          durationInMinutes: ev.durationInMinutes || 60,
          meetingProvider: ev.meetingProvider,
          shareLinkId: ev.meetingLinkId,
          countdown,
          isLive,
          canJoin,
        });
      }
    }

    this.todaySessionsCount = todayCount;

    list.sort((a, b) => {
      if (a.isLive && !b.isLive) return -1;
      if (!a.isLive && b.isLive) return 1;
      return a.startsAt.getTime() - b.startsAt.getTime();
    });

    this.nextSession = list.length > 0 ? list[0] : null;
  }

  launchSession(session: NextSessionItem): void {
    if (session.directJoinUrl?.trim()) {
      window.open(session.directJoinUrl.trim(), '_blank', 'noopener,noreferrer');
      return;
    }

    if (session.shareLinkId) {
      const provider = (session.meetingProvider || 'DYTE').toUpperCase();
      if (this.isTeachingTab && provider === 'ZOOM') {
        this.liveClassService
          .getZoomMeetingInfoFromAPI(session.shareLinkId)
          .subscribe({
            next: (rawData) => {
              window.open(
                rawData?.start_url || `./meetings/join/zoom/${session.shareLinkId}`,
                '_blank',
                'noopener,noreferrer',
              );
            },
            error: () => {
              window.open(
                `./meetings/join/zoom/${session.shareLinkId}`,
                '_blank',
                'noopener,noreferrer',
              );
            },
          });
        return;
      }

      window.open(
        `./meetings/join/${provider.toLowerCase()}/${session.shareLinkId}`,
        '_blank',
        'noopener,noreferrer',
      );
      return;
    }

    this.router.navigate(
      this.isTeachingTab
        ? ['/dashboard/instructor/live-class', session.liveClassId]
        : ['/dashboard/learning/live-class', session.liveClassId],
    );
  }

  formatSessionTime(date: Date, durationInMinutes: number): string {
    const startStr = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
    const end = new Date(
      date.getTime() + (durationInMinutes || 60) * 60 * 1000,
    );
    const endStr = end.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
    const isToday = new Date().toDateString() === date.toDateString();
    const datePrefix = isToday
      ? 'Today'
      : date.toLocaleDateString([], {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        });
    return `${datePrefix} • ${startStr} - ${endStr}`;
  }

  get isTeachingTab(): boolean {
    return this.isInstructor && this.activeRoleTab === 'TEACHING';
  }

  get currentHeroSession(): NextSessionItem | null {
    return this.isTeachingTab ? this.nextTeachingSession : this.nextSession;
  }

  get fullName(): string {
    if (this.user?.name) {
      return `${this.user.name.firstName} ${this.user.name.lastName}`;
    }
    return 'User';
  }

  get userInitial(): string {
    if (this.user?.name?.firstName) {
      return this.user.name.firstName.charAt(0).toUpperCase();
    }
    return 'U';
  }

  get isInstructor(): boolean {
    return (
      this.user?.role === UserRoles.INSTRUCTOR ||
      this.user?.role === UserRoles.ADMIN
    );
  }

  get showCourses(): boolean {
    return this.currentTenant?.settings?.features?.courses !== false;
  }

  get todayDateString(): string {
    return new Date().toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  }
}

